import {
  useGetIdentity,
  useGetList,
  useGetMany,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import type { Contact, OutreachList, OutreachListMember } from "../types";

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export const OutreachListMembersTable = ({ list }: { list: OutreachList }) => {
  const translate = useTranslate();
  const { data: members, isPending } = useGetList<OutreachListMember>(
    "outreach_list_members",
    {
      filter: { list_id: list.id, "removed_at@is": null },
      sort: { field: "added_at", order: "DESC" },
      pagination: { page: 1, perPage: 200 },
    },
  );

  const contactIds = members?.map((m) => m.contact_id) ?? [];
  const { data: contacts } = useGetMany<Contact>(
    "contacts_summary",
    { ids: contactIds },
    { enabled: contactIds.length > 0 },
  );
  const contactsById = new Map((contacts ?? []).map((c) => [String(c.id), c]));

  if (isPending) return null;

  if (!members?.length) {
    return (
      <p className="text-muted-foreground text-sm italic py-4">
        {translate("resources.outreach_lists.no_members", {
          _: "No active members in this list yet.",
        })}
      </p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>
              {translate("resources.contacts.name", { smart_count: 1 })}
            </TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Added by</TableHead>
            <TableHead>Added</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              contact={contactsById.get(String(member.contact_id))}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

const MemberRow = ({
  member,
  contact,
}: {
  member: OutreachListMember;
  contact?: Contact;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [update, { isPending }] = useUpdate();

  const handleRemove = () => {
    update(
      "outreach_list_members",
      {
        id: member.id,
        data: {
          removed_at: new Date().toISOString(),
          removed_by: identity?.fullName ?? "manual",
        },
        previousData: member,
      },
      {
        onSuccess: () =>
          notify("resources.outreach_lists.action.remove_success", {
            type: "success",
            messageArgs: { _: "Contact removed from the list" },
          }),
        onError: () =>
          notify("resources.outreach_lists.errors.remove_failed", {
            type: "error",
            messageArgs: { _: "Failed to remove contact from the list" },
          }),
      },
    );
  };

  const email = contact?.email_jsonb?.[0]?.email;

  return (
    <TableRow>
      <TableCell>
        {contact ? (
          <Link
            to={`/contacts/${contact.id}/show`}
            className="hover:underline"
          >
            {contact.first_name} {contact.last_name}
          </Link>
        ) : (
          `#${member.contact_id}`
        )}
      </TableCell>
      <TableCell className="text-muted-foreground">{email ?? "-"}</TableCell>
      <TableCell className="text-muted-foreground">
        {member.added_by}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {fmtDate(member.added_at)}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRemove}
          disabled={isPending}
        >
          {translate("resources.outreach_lists.action.remove", {
            _: "Remove",
          })}
        </Button>
      </TableCell>
    </TableRow>
  );
};
